import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangePicker.css';
import { PRESETS, presetRange, detectPreset, formatRange, todayEt, ymdToDate, dateToYmd } from '../../utils/dateRange';

const MOBILE_QUERY = '(max-width: 767px)';
const useIsMobile = () => {
    const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(MOBILE_QUERY).matches : false);
    const [mobile, setMobile] = useState(get);
    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mq = window.matchMedia(MOBILE_QUERY);
        const on = () => setMobile(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', on);
        else if (mq.addListener) mq.addListener(on);
        return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener && mq.removeListener(on));
    }, []);
    return mobile;
};

const CalendarIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M7 2h2v2h6V2h2v2h3a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3V2zm12 8H5v9h14v-9zM5 8h14V6H5v2z" />
    </svg>
);

/** Words-above-results line: "Showing: Oct 1 – Oct 29, 2026 (Eastern Time)". */
export const DateRangeSummary = ({ startDate, endDate, prefix = 'Showing' }) => (
    <p className="drp-summary" aria-live="polite" data-testid="date-range-summary">
        {prefix}: <strong>{formatRange(startDate, endDate)}</strong> <span className="text-muted">(Eastern Time)</span>
    </p>
);

/**
 * Shared date-range filter. Values are 'YYYY-MM-DD' ET calendar days; onChange({ startDate, endDate, preset }).
 * Click the field or the calendar icon to open; presets apply immediately; Custom = pick start then end day.
 * Esc closes and returns focus to the field. Two months on desktop, one on mobile.
 */
const DateRangePicker = ({ startDate, endDate, onChange, label = 'Date range', maxDate, id = 'date-range', className = '' }) => {
    const [open, setOpen] = useState(false);
    const [custom, setCustom] = useState(false);
    const [draft, setDraft] = useState([ymdToDate(startDate), ymdToDate(endDate)]);
    const root = useRef(null);
    const field = useRef(null);
    const isMobile = useIsMobile();
    const max = maxDate === null ? null : maxDate || todayEt();
    const active = custom ? 'custom' : detectPreset(startDate, endDate);

    useEffect(() => {
        setDraft([ymdToDate(startDate), ymdToDate(endDate)]);
    }, [startDate, endDate]);

    useEffect(() => {
        if (!open) return undefined;
        const away = (e) => {
            if (root.current && !root.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', away);
        return () => document.removeEventListener('mousedown', away);
    }, [open]);

    const close = () => {
        setOpen(false);
        if (field.current) field.current.focus();
    };
    const apply = (s, e, preset) => {
        setCustom(preset === 'custom');
        onChange({ startDate: s, endDate: e, preset });
    };
    const pickPreset = (key) => {
        if (key === 'custom') {
            setCustom(true);
            setDraft([null, null]);
            return;
        }
        const r = presetRange(key);
        apply(r.startDate, r.endDate, key);
        close();
    };
    const pickDays = ([s, e]) => {
        setDraft([s, e]);
        if (s && e) {
            apply(dateToYmd(s), dateToYmd(e), 'custom');
            close();
        }
    };
    const presetLabel = (PRESETS.find((p) => p.key === active) || {}).label;

    return (
        <div className={`drp ${className}`} ref={root} onKeyDown={(e) => e.key === 'Escape' && open && (e.stopPropagation(), close())}>
            <span className="drp-label" id={`${id}-label`}>{label}</span>
            <div className="drp-field">
                <button
                    type="button"
                    ref={field}
                    id={id}
                    className="form-control drp-input"
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    aria-labelledby={`${id}-label ${id}`}
                    onClick={() => setOpen((o) => !o)}
                >
                    {formatRange(startDate, endDate)}
                    {presetLabel && <span className="drp-tag">{presetLabel}</span>}
                </button>
                <button type="button" className="drp-icon" aria-label={`Open calendar for ${label}`} onClick={() => setOpen((o) => !o)}>
                    <CalendarIcon />
                </button>
            </div>
            {open && (
                <div className="drp-popup" role="dialog" aria-label={`Choose ${label}`} data-testid="drp-popup">
                    <div className="drp-presets" role="group" aria-label="Quick ranges">
                        {PRESETS.map((p) => (
                            <button key={p.key} type="button" className="drp-preset" aria-pressed={active === p.key} onClick={() => pickPreset(p.key)}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div>
                        <DatePicker
                            inline
                            selectsRange
                            startDate={draft[0]}
                            endDate={draft[1]}
                            selected={draft[0]}
                            onChange={pickDays}
                            monthsShown={isMobile ? 1 : 2}
                            openToDate={draft[1] || draft[0] || undefined}
                            maxDate={max ? ymdToDate(max) : undefined}
                            calendarStartDay={0}
                        />
                        <div className="drp-footer">
                            <span>{draft[0] && !draft[1] ? 'Now pick the end day' : 'Pick a start day, then an end day'}</span>
                            <button type="button" className="btn btn-sm btn-light" onClick={close}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;
